import React, { useMemo, useState } from 'react';
import {
  BarChart2,
  TrendingUp,
  Users,
  UserCircle2,
  MessageSquare,
  Zap,
  RefreshCw,
  Filter,
  X,
} from 'lucide-react';
import {
  useUsageSummary,
  useTenantUsage,
  type TenantGroupedRow,
} from '../../../../api/pleri-usage';
import { LoadingSpinner } from '../ToolExecution/LoadingSpinner';
import { Select } from '@plerionhq/design-system/experimental';
import type { TFunction } from 'i18next';
import {
  formatNumber,
  formatShortDate,
  allDaysInRange,
  getPeriodForMonth,
  getWorkspaceLabel,
  getIdentityTypeLabel,
  MONTH_OPTIONS,
  SectionHeading,
  StatBlock,
  TrendSection,
  HierarchyBreakdownTable,
  type HierarchyRow,
} from './UsageDashboard';

import { useTranslation, Trans } from 'react-i18next';

const ALL_VALUE = '__all__';
const TREND_TOP_N = 8;

type BreakdownGroupBy =
  | 'user'
  | 'workspace'
  | 'identityType'
  | 'userWorkspace';

const breakdownGroupOptions = (
  t: TFunction<'pleri/settings'>,
): { value: BreakdownGroupBy; label: string }[] => [
  { value: 'userWorkspace', label: t('userEngagement') },
  { value: 'user', label: t('common:user') },
  { value: 'workspace', label: t('pleriEngagement') },
  { value: 'identityType', label: t('identityType') },
];

type TrendBreakdownBy = 'none' | 'user' | 'workspaceType';

const trendBreakdownOptions = (
  t: TFunction<'pleri/settings'>,
): { value: TrendBreakdownBy; label: string }[] => [
  { value: 'none', label: t('none') },
  { value: 'user', label: t('common:user') },
  { value: 'workspaceType', label: t('pleriEngagement') },
];

interface Counters {
  totalTokens: number;
  messageCount: number;
  conversationCount: number;
}
const newCounter = (): Counters => ({
  totalTokens: 0,
  messageCount: 0,
  conversationCount: 0,
});

function addRowCounts(target: Counters, row: TenantGroupedRow): void {
  target.totalTokens += row.totalTokens;
  target.messageCount += row.messageCount;
  target.conversationCount += row.conversationCount;
}

function matchesUserFilter(
  row: TenantGroupedRow,
  filterUserName: string,
): boolean {
  if (filterUserName === ALL_VALUE) {
    return true;
  }
  const name = row.identityName ?? '';
  return name === filterUserName;
}

export const UsageDashboardPage: React.FC = () => {
  const { t } = useTranslation('pleri/settings');
  const now = useMemo(() => new Date(), []);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(() => {
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    return `${y}-${String(m).padStart(2, '0')}`;
  });

  const [filterUser, setFilterUser] = useState<string>(ALL_VALUE);
  const [filterSource, setFilterSource] = useState<string>(ALL_VALUE);

  const [breakdownGroup, setBreakdownGroup] =
    useState<BreakdownGroupBy>('userWorkspace');
  const [trendBreakdown, setTrendBreakdown] =
    useState<TrendBreakdownBy>('none');

  const period = useMemo(() => {
    const [y, m] = selectedMonthKey.split('-').map(Number);
    return getPeriodForMonth(y, m);
  }, [selectedMonthKey]);

  const summaryParams = useMemo(
    () => ({
      startDate: period.start,
      endDate: period.end,
    }),
    [period.start, period.end],
  );

  // Source goes via the backend workspaceType param. User filter is applied
  // client-side from identityName because the tenant endpoint scopes by current
  // tenant and doesn't take an identityId param yet.
  const sourceParam =
    filterSource === ALL_VALUE ? undefined : filterSource;
  const userActive = filterUser !== ALL_VALUE;

  // Tenant data for the breakdown table — we always include identityName in
  // groupBy when the user filter is on, so we can do client-side filtering
  // before aggregating into the requested view.
  const breakdownParams = useMemo(() => {
    const dims = new Set<string>();
    if (breakdownGroup === 'user') {
      dims.add('identityName');
    } else if (breakdownGroup === 'workspace') {
      dims.add('workspaceType');
    } else if (breakdownGroup === 'identityType') {
      dims.add('identityType');
    } else {
      dims.add('identityName');
      dims.add('workspaceType');
    }
    if (userActive) {
      dims.add('identityName');
    }
    const groupBy = ['date', ...Array.from(dims)].join(',');
    return {
      startDate: period.start,
      endDate: period.end,
      workspaceType: sourceParam,
      groupBy,
    };
  }, [period.start, period.end, breakdownGroup, sourceParam, userActive]);

  const trendParams = useMemo(() => {
    const dims = new Set<string>();
    if (trendBreakdown === 'user') {
      dims.add('identityName');
    } else if (trendBreakdown === 'workspaceType') {
      dims.add('workspaceType');
    }
    if (userActive) {
      dims.add('identityName');
    }
    const groupBy =
      dims.size === 0 ? 'date' : ['date', ...Array.from(dims)].join(',');
    return {
      startDate: period.start,
      endDate: period.end,
      workspaceType: sourceParam,
      groupBy,
    };
  }, [period.start, period.end, trendBreakdown, sourceParam, userActive]);

  // Universe of dim combinations present in the selected period (no filters
  // applied). Drives filter dropdown options and active-user count so the
  // dropdowns only ever show values that actually exist in the data.
  const filterUniverseParams = useMemo(
    () => ({
      startDate: period.start,
      endDate: period.end,
      groupBy: 'identityName,identityType,workspaceType',
    }),
    [period.start, period.end],
  );

  const { data: summaryData, isLoading: summaryLoading } =
    useUsageSummary(summaryParams);
  const { data: breakdownData, isLoading: breakdownLoading } = useTenantUsage(
    breakdownParams,
    true,
  );
  const { data: trendData } = useTenantUsage(trendParams, true);
  const { data: filterUniverseData } = useTenantUsage(
    filterUniverseParams,
    true,
  );

  const summary = summaryData?.data;

  const filterUniverseRows = useMemo<TenantGroupedRow[]>(() => {
    const rows = filterUniverseData?.data;
    if (!rows || !Array.isArray(rows)) {
      return [];
    }
    return rows as TenantGroupedRow[];
  }, [filterUniverseData?.data]);

  // Each filter's options are derived from the universe rows, narrowed by the
  // OTHER active filter. Currently-selected values are always preserved in
  // the list, even if narrowed out, so the Select doesn't glitch.

  const userFilterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of filterUniverseRows) {
      if (filterSource !== ALL_VALUE && r.workspaceType !== filterSource) {
        continue;
      }
      if (r.identityName) {
        names.add(r.identityName);
      }
    }
    if (filterUser !== ALL_VALUE && !names.has(filterUser)) {
      names.add(filterUser);
    }
    const opts: { label: string; value: string }[] = [
      { label: t('allUsers'), value: ALL_VALUE },
    ];
    Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .forEach((n) => opts.push({ label: n, value: n }));
    return opts;
  }, [filterUniverseRows, filterSource, filterUser, t]);

  const sourceFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const r of filterUniverseRows) {
      if (filterUser !== ALL_VALUE && r.identityName !== filterUser) {
        continue;
      }
      if (r.workspaceType) {
        seen.add(r.workspaceType);
      }
    }
    if (filterSource !== ALL_VALUE && !seen.has(filterSource)) {
      seen.add(filterSource);
    }
    const opts: { label: string; value: string }[] = [
      { label: t('allSources'), value: ALL_VALUE },
    ];
    Array.from(seen)
      .map((ws) => ({ value: ws, label: getWorkspaceLabel(t, ws) }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((s) => opts.push(s));
    return opts;
  }, [filterUniverseRows, filterUser, filterSource, t]);

  const activeUserCount = useMemo(() => {
    const names = new Set<string>();
    for (const r of filterUniverseRows) {
      if (r.identityName) {
        names.add(r.identityName);
      }
    }
    return names.size;
  }, [filterUniverseRows]);

  // --- Breakdown tree ---------------------------------------------------

  const breakdownTree = useMemo<HierarchyRow[]>(() => {
    const rows = breakdownData?.data;
    if (!rows || !Array.isArray(rows)) {
      return [];
    }
    const filtered = (rows as TenantGroupedRow[]).filter((r) =>
      matchesUserFilter(r, filterUser),
    );

    if (breakdownGroup === 'user') {
      const acc = new Map<string, Counters & { id: string }>();
      for (const r of filtered) {
        const name = r.identityName ?? 'Unknown';
        const cur = acc.get(name) ?? { id: name, ...newCounter() };
        addRowCounts(cur, r);
        acc.set(name, cur);
      }
      return Array.from(acc.values())
        .sort((a, b) => b.totalTokens - a.totalTokens)
        .map((v) => ({
          id: v.id,
          label: v.id,
          totalTokens: v.totalTokens,
          messageCount: v.messageCount,
          conversationCount: v.conversationCount,
        }));
    }

    if (breakdownGroup === 'workspace') {
      const acc = new Map<string, Counters & { id: string }>();
      for (const r of filtered) {
        const key = String(r.workspaceType ?? 'Unknown');
        const cur = acc.get(key) ?? { id: key, ...newCounter() };
        addRowCounts(cur, r);
        acc.set(key, cur);
      }
      return Array.from(acc.values())
        .sort((a, b) => b.totalTokens - a.totalTokens)
        .map((v) => ({
          id: v.id,
          label: getWorkspaceLabel(t, v.id),
          totalTokens: v.totalTokens,
          messageCount: v.messageCount,
          conversationCount: v.conversationCount,
        }));
    }

    if (breakdownGroup === 'identityType') {
      const acc = new Map<string, Counters & { id: string }>();
      for (const r of filtered) {
        const key = String(r.identityType ?? 'Unknown');
        const cur = acc.get(key) ?? { id: key, ...newCounter() };
        addRowCounts(cur, r);
        acc.set(key, cur);
      }
      return Array.from(acc.values())
        .sort((a, b) => b.totalTokens - a.totalTokens)
        .map((v) => ({
          id: v.id,
          label: getIdentityTypeLabel(t, v.id),
          totalTokens: v.totalTokens,
          messageCount: v.messageCount,
          conversationCount: v.conversationCount,
        }));
    }

    // Hierarchical: User › Engagement
    interface UserNode {
      totals: Counters;
      identityType?: string;
      workspaces: Map<string, Counters>;
    }
    const byUser = new Map<string, UserNode>();
    for (const r of filtered) {
      const name = r.identityName ?? 'Unknown';
      const ws = String(r.workspaceType ?? 'Unknown');
      let uNode = byUser.get(name);
      if (!uNode) {
        uNode = {
          totals: newCounter(),
          identityType: r.identityType ?? undefined,
          workspaces: new Map(),
        };
        byUser.set(name, uNode);
      }
      addRowCounts(uNode.totals, r);
      if (!uNode.identityType && r.identityType) {
        uNode.identityType = r.identityType;
      }
      const wCur = uNode.workspaces.get(ws) ?? newCounter();
      addRowCounts(wCur, r);
      uNode.workspaces.set(ws, wCur);
    }
    return Array.from(byUser.entries())
      .map(([name, uNode]) => ({
        id: name,
        label: name,
        hint: uNode.identityType
          ? getIdentityTypeLabel(t, uNode.identityType)
          : undefined,
        totalTokens: uNode.totals.totalTokens,
        messageCount: uNode.totals.messageCount,
        conversationCount: uNode.totals.conversationCount,
        children: Array.from(uNode.workspaces.entries())
          .map(([ws, wCounter]) => ({
            id: ws,
            label: getWorkspaceLabel(t, ws),
            totalTokens: wCounter.totalTokens,
            messageCount: wCounter.messageCount,
            conversationCount: wCounter.conversationCount,
          }))
          .sort((a, b) => b.totalTokens - a.totalTokens),
      }))
      .sort((a, b) => b.totalTokens - a.totalTokens);
  }, [breakdownData?.data, breakdownGroup, filterUser]);

  // --- Trend chart data ------------------------------------------------

  const { trendChartData, trendSeriesKeys, trendSeriesLabels } = useMemo(() => {
    const days = allDaysInRange(period.start, period.end);
    const emptyForUngrouped = days.map((date) => ({
      label: formatShortDate(date),
      totalTokens: 0,
      messageCount: 0,
      conversationCount: 0,
    }));

    const rows = trendData?.data;
    if (!rows || !Array.isArray(rows)) {
      return {
        trendChartData: emptyForUngrouped,
        trendSeriesKeys: null as string[] | null,
        trendSeriesLabels: null as string[] | null,
      };
    }
    const filtered = (rows as TenantGroupedRow[]).filter((r) =>
      matchesUserFilter(r, filterUser),
    );

    if (trendBreakdown === 'none') {
      const byDate = new Map<string, Counters>();
      for (const date of days) {
        byDate.set(date, newCounter());
      }
      for (const r of filtered) {
        if (!r.usageDate) {
          continue;
        }
        const cur = byDate.get(r.usageDate);
        if (cur) {
          cur.totalTokens += r.totalTokens;
          cur.messageCount += r.messageCount;
          cur.conversationCount += r.conversationCount;
        }
      }
      const data = days.map((date) => ({
        label: formatShortDate(date),
        totalTokens: byDate.get(date)?.totalTokens ?? 0,
        messageCount: byDate.get(date)?.messageCount ?? 0,
        conversationCount: byDate.get(date)?.conversationCount ?? 0,
      }));
      return {
        trendChartData: data,
        trendSeriesKeys: null,
        trendSeriesLabels: null,
      };
    }

    const dimValue = (r: TenantGroupedRow): string => {
      if (trendBreakdown === 'user') {
        return r.identityName ?? 'Unknown';
      }
      return String(r.workspaceType ?? 'Unknown');
    };

    const seriesTotals = new Map<string, number>();
    for (const r of filtered) {
      const v = dimValue(r);
      seriesTotals.set(v, (seriesTotals.get(v) ?? 0) + r.totalTokens);
    }
    const top = Array.from(seriesTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, TREND_TOP_N)
      .map(([k]) => k);
    const topSet = new Set(top);
    const dimensionValues =
      topSet.size < seriesTotals.size ? [...top, 'Other'] : [...top];

    const byDateAndDim = new Map<string, Map<string, Counters>>();
    for (const date of days) {
      byDateAndDim.set(date, new Map());
    }
    for (const r of filtered) {
      if (!r.usageDate) {
        continue;
      }
      const v = dimValue(r);
      const bucket = topSet.has(v) ? v : 'Other';
      const dayMap = byDateAndDim.get(r.usageDate);
      if (!dayMap) {
        continue;
      }
      const cur = dayMap.get(bucket) ?? newCounter();
      cur.totalTokens += r.totalTokens;
      cur.messageCount += r.messageCount;
      cur.conversationCount += r.conversationCount;
      dayMap.set(bucket, cur);
    }
    const safeKeys = dimensionValues.map((_, i) => `series_${i}`);
    const labelToKey = new Map<string, string>(
      dimensionValues.map((label, i) => [label, safeKeys[i]]),
    );
    const data = days.map((date) => {
      const dayMap = byDateAndDim.get(date) ?? new Map();
      const point: Record<
        string,
        | string
        | number
        | {
            totalTokens: number;
            messageCount: number;
            conversationCount: number;
          }
      > = {
        label: formatShortDate(date),
        totalTokens: 0,
        messageCount: 0,
        conversationCount: 0,
      };
      for (const dimLabel of dimensionValues) {
        const key = labelToKey.get(dimLabel);
        if (!key) {
          continue;
        }
        const v = dayMap.get(dimLabel) ?? newCounter();
        point[key] = {
          totalTokens: v.totalTokens,
          messageCount: v.messageCount,
          conversationCount: v.conversationCount,
        };
        point.totalTokens = Number(point.totalTokens ?? 0) + v.totalTokens;
        point.messageCount = Number(point.messageCount ?? 0) + v.messageCount;
        point.conversationCount =
          Number(point.conversationCount ?? 0) + v.conversationCount;
      }
      return point;
    });
    const seriesLabels = dimensionValues.map((v) =>
      trendBreakdown === 'workspaceType' ? getWorkspaceLabel(t, v) : v,
    );
    return {
      trendChartData: data,
      trendSeriesKeys: safeKeys,
      trendSeriesLabels: seriesLabels,
    };
  }, [period.start, period.end, trendBreakdown, trendData?.data, filterUser]);

  const periodLabel = `${formatShortDate(period.start)} – ${formatShortDate(period.end)}`;

  const monthOptions = useMemo(
    () =>
      MONTH_OPTIONS.map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
    [],
  );

  const filtersActive =
    filterUser !== ALL_VALUE || filterSource !== ALL_VALUE;

  const clearFilters = () => {
    setFilterUser(ALL_VALUE);
    setFilterSource(ALL_VALUE);
  };

  const filterSummaryLabel = useMemo(() => {
    const parts: string[] = [];
    if (filterUser !== ALL_VALUE) {
      parts.push(t('userFilter', { user: filterUser }));
    }
    if (filterSource !== ALL_VALUE) {
      parts.push(
        t('sourceFilter', { source: getWorkspaceLabel(t, filterSource) }),
      );
    }
    return parts.length === 0 ? t('allUsersFilter') : parts.join(' · ');
  }, [filterUser, filterSource, t]);

  const primaryHeaderByDepth =
    breakdownGroup === 'userWorkspace'
      ? [t('common:user'), t('engagement')]
      : breakdownGroup === 'user'
        ? [t('common:user')]
        : breakdownGroup === 'workspace'
          ? [t('engagement')]
          : ['Identity type'];

  if (summaryLoading) {
    return (
      <div className="flex h-full w-full flex-col overflow-y-auto bg-white dark:bg-gray-900">
        <div className="mx-auto w-full max-w-4xl flex-1 px-6 py-12 sm:px-8">
          <div className="flex min-h-[320px] items-center justify-center">
            <LoadingSpinner text={t('loadingUsage')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-white dark:bg-gray-900">
      <div className="mx-auto w-full max-w-4xl flex-1 px-6 py-12 sm:px-8">
        <header className="mb-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100 leading-tight">
              {t('usage')}
            </h1>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              {periodLabel}
            </p>
          </div>
          <label className="flex shrink-0 items-center gap-2">
            <span className="text-sm leading-none font-medium text-gray-700 dark:text-gray-300">
              {t('month')}
            </span>
            <Select
              options={monthOptions}
              defaultValue={selectedMonthKey}
              onSelect={(selected: { label: string; value: string }) =>
                setSelectedMonthKey(selected.value)
              }
              placeholder={t('selectMonth')}
              className="!w-[220px]"
            />
          </label>
        </header>

        {summary && (
          <section className="mt-6 border-t border-gray-100 pt-6 pb-6 dark:border-gray-800">
            <SectionHeading icon={BarChart2} label={t('stats')} />
            <div className="mt-5 flex flex-wrap divide-x divide-gray-100 dark:divide-gray-800">
              <StatBlock
                icon={Zap}
                label={t('tokens')}
                value={formatNumber(summary.summary.totalTokens)}
                className="pr-5 sm:pr-6"
              />
              <StatBlock
                icon={MessageSquare}
                label={t('questionsAsked')}
                value={formatNumber(
                  summary.summary.questionsAsked?.totalMessages ??
                    summary.summary.totalMessages,
                )}
                className="px-5 sm:px-6"
              />
              <StatBlock
                icon={RefreshCw}
                label={t('recurringTasks')}
                value={formatNumber(summary.summary.tasks?.totalMessages ?? 0)}
                className="px-5 sm:px-6"
              />
              <StatBlock
                icon={Users}
                label={t('activeUsers')}
                value={formatNumber(activeUserCount)}
                className="pl-5 sm:pl-6"
              />
            </div>
          </section>
        )}

        <section className="mt-6 border-t border-gray-100 pt-5 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Filter
                className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"
                strokeWidth={2}
              />
              {t('filters')}
            </h2>
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
                {t('clearFilters')}
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-[260px]">
              <span className="text-xs leading-none font-medium text-gray-600 dark:text-gray-400">
                {t('common:user')}
              </span>
              <Select
                options={userFilterOptions}
                defaultValue={filterUser}
                onSelect={(selected: { label: string; value: string }) =>
                  setFilterUser(selected.value)
                }
                placeholder={t('allUsers')}
                className="!w-full"
              />
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-[260px]">
              <span className="text-xs leading-none font-medium text-gray-600 dark:text-gray-400">
                {t('source')}
              </span>
              <Select
                options={sourceFilterOptions}
                defaultValue={filterSource}
                onSelect={(selected: { label: string; value: string }) =>
                  setFilterSource(selected.value)
                }
                placeholder={t('allSources')}
                className="!w-full"
              />
            </label>
          </div>
          {filtersActive && (
            <p className="mt-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              <Trans
                t={t}
                i18nKey="trendBreakdownBelowShowDataFor"
                values={{ filterSummaryLabel }}
                components={{
                  span: (
                    <span className="font-medium text-gray-700 dark:text-gray-300" />
                  ),
                }}
              />
            </p>
          )}
        </section>

        <section className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionHeading icon={TrendingUp} label={t('trend')} />
            <label className="flex shrink-0 items-center gap-2">
              <span className="text-sm leading-none font-medium text-gray-700 dark:text-gray-300">
                {t('breakdownBy')}
              </span>
              <Select
                options={trendBreakdownOptions(t)}
                defaultValue={trendBreakdown}
                onSelect={(selected: {
                  label: string;
                  value: TrendBreakdownBy;
                }) => setTrendBreakdown(selected.value)}
                placeholder={t('none')}
                className="!w-[220px]"
              />
            </label>
          </div>
          <div className="mt-5">
            <TrendSection
              data={trendChartData}
              seriesKeys={trendSeriesKeys}
              seriesLabels={trendSeriesLabels}
            />
            {trendBreakdown !== 'none' && trendSeriesLabels && (
              <p className="mt-2 text-xs leading-relaxed text-gray-400 dark:text-gray-500">
                {t('showingTopSeriesTheRestAreGrouped', { TREND_TOP_N })}
              </p>
            )}
          </div>
        </section>

        <section className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionHeading icon={UserCircle2} label={t('breakdown')} />
            <label className="flex shrink-0 items-center gap-2">
              <span className="text-sm leading-none font-medium text-gray-700 dark:text-gray-300">
                {t('groupBy')}
              </span>
              <Select
                options={breakdownGroupOptions(t)}
                defaultValue={breakdownGroup}
                onSelect={(selected: {
                  label: string;
                  value: BreakdownGroupBy;
                }) => setBreakdownGroup(selected.value)}
                placeholder={t('userEngagement')}
                className="!w-[240px]"
              />
            </label>
          </div>
          <div className="mt-5">
            <HierarchyBreakdownTable
              rows={breakdownTree}
              primaryHeaderByDepth={primaryHeaderByDepth}
              isLoading={breakdownLoading}
              emptyLabel={t('noUsageRecordedForIn', {
                filterSummaryLabel,
                periodLabel,
              })}
            />
          </div>
        </section>

        <div className="h-6" />
      </div>
    </div>
  );
};
